<template>
  <Transition name="file-preview">
    <section
      v-if="isVisible"
      class="absolute inset-0 z-50 flex flex-col text-slate-100 bg-white/70 dark:bg-[var(--p-surface-900)]/30 backdrop-blur-xl ml-0.5"
    >
      <header
        class="flex min-h-14 shrink-0 items-center gap-3 border-t dark:border-none border-[var(--p-primary-100)] px-3 py-2  "
      >
        <div class="flex shrink-0 items-center gap-1">
          <Button
            text
            rounded
            class="min-h-8 min-w-8 flex-shrink-0 text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)] rounded-full p-0.5!"
            title="Cerrar"
            @click="requestClose"
          >
            <X :size="20" class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)]" />
          </Button>
        </div>

        <div v-if="isCurrentImage" class="min-w-0 flex-1">
          <EditorToolbar
            :active-tool="activeTool"
            :color="editorColor"
            :brush-width="brushWidth"
            :font-size="fontSize"
            :font-family="fontFamily"
            :text-align="textAlign"
            :crop-ratio="cropRatio"
            :censor-mode="censorMode"
            :censor-level="censorLevel"
            :apply-tick="applyTick"
            :open-text-panel-tick="openTextPanelTick"
            @tool="setTool"
            @filter="applyFilter"
            @color="setEditorColor"
            @brush-width="setBrushWidth"
            @font-size="setFontSize"
            @font-family="setFontFamily"
            @text-align="setTextAlign"
            @crop-ratio="setCropRatio"
            @rotate-left="rotateLeft"
            @rotate-right="rotateRight"
            @crop-undo="undoCrop"
            @emoji="addEmoji"
            @censor-level="setCensorLevel"
            @censor-mode="setCensorMode"
          />
        </div>

        <div v-if="isCurrentImage" class="flex shrink-0 items-center gap-1">
          <Button
            text
            rounded
            class="min-h-8 min-w-8 flex-shrink-0 dark:text-[var(--p-surface-200)] rounded-full p-0.5! disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!historyState.canUndo"
            title="Deshacer"
            @click="imageCanvasRef?.undo()"
          >
            <Undo2
              :size="18"
              class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]"
            />
          </Button>
          <Button
            text
            rounded
            class="min-h-8 min-w-8 flex-shrink-0 text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)] rounded-full p-0.5! disabled:cursor-not-allowed disabled:opacity-40"
            :disabled="!historyState.canRedo"
            title="Rehacer"
            @click="imageCanvasRef?.redo()"
          >
            <Redo2 :size="18" class="text-[var(--p-surface-500)] dark:text-white" />
          </Button>
          <Button
            v-if="hasUnappliedChanges"
            text
            rounded
            class="min-h-8 min-w-8 flex-shrink-0 text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)] rounded-full p-0.5! disabled:cursor-not-allowed disabled:opacity-40"
            title="Aplicar cambios"
            @click="applyChanges"
          >
            <Check :size="18" class="text-[var(--p-surface-500)] dark:text-white" />
          </Button>
        </div>
      </header>

      <div v-if="validFiles.length > 0 && currentFile" class="flex min-h-0 flex-1 flex-col">
        <div v-if="isCurrentImage" class="relative min-h-0 flex-1">
          <ImageCanvas
            :key="currentFileKey"
            ref="imageCanvasRef"
            :file="currentFile"
            :active-tool="activeTool"
            :color="editorColor"
            :brush-width="brushWidth"
            :font-size="fontSize"
            :font-family="fontFamily"
            :text-align="textAlign"
            :crop-ratio="cropRatio"
            :censor-mode="censorMode"
            @ready="historyState = $event"
            @history="historyState = $event"
            @request-apply="handleCanvasOutsidePress"
            @text-edit="openTextPanelForSelectedText"
            @text-style="syncSelectedTextStyle"
            @censor-style="syncSelectedCensorStyle"
            @selection-change="selectedCanvasItem = $event"
          />
        </div>

        <div
          v-else
          class="relative flex min-h-0 flex-1 items-center justify-center overflow-auto p-4"
        >
          <div
            class="flex max-h-full w-full max-w-xl flex-col items-center justify-center py-5 text-center"
            :class="
              !isVideo(currentFile) &&
              'rounded-lg bg-white/30 shadow-lg dark:bg-[var(--p-primary-900)]/30'
            "
          >
            <video
              v-if="isVideo(currentFile)"
              :src="getFilePreviewUrl(currentFile)"
              class="block max-h-[55vh] max-w-full rounded-md object-contain shadow-lg/10"
              controls
            />
            <template v-else-if="isAudio(currentFile)">
              <div class="mb-4 rounded-full bg-emerald-600 p-5 shadow-lg/10">
                <Music :size="42" :stroke-width="1.5" />
              </div>
              <audio :src="getFilePreviewUrl(currentFile)" class="w-full max-w-md" controls />
            </template>
            <template v-else>
              <component
                :is="getFileIcon(currentFile)"
                :size="44"
                :stroke-width="1.5"
                class="mb-4 text-slate-400 dark:text-[var(--p-surface-300)]"
              />
              <p
                class="max-w-full truncate text-sm font-medium text-[var(--p-surface-500)] dark:text-white"
              >
                {{ currentFile.name }}
              </p>
            </template>
          </div>
        </div>
        <div class="flex min-h-[64px] shrink-0 items-center justify-center pt-5">
          <Transition name="fade-slide">
            <div
              v-show="showSendControls"
              class="flex w-full lg:w-3/4 items-center justify-center gap-2 rounded-lg px-2"
            >
              <AppTextarea
                v-model="currentCaption"
                :placeholder="$t('whatsapp.message_input.placeholder')"
                :autoResize="true"
                :rows="1"
                :useFloatLabel="false"
                class="mt-1 max-h-26! overflow-y-auto! rounded-lg! py-2! text-sm!"
              />
              <EmojiPicker v-if="showSendControls" position="top" @emoji="appendCaptionEmoji" />
            </div>
          </Transition>
        </div>

        <footer
          class="relative min-h-[81px] shrink-0 border-t border-[var(--p-surface-200)]  p-3 dark:border-[var(--p-surface-800)] "
        >
          <div
            v-if="showDeleteControl"
            class="flex min-h-14 items-center justify-center transition-all duration-300"
          >
            <Transition name="fade-slide">
              <Button
                text
                rounded
                class="min-h-8 min-w-8 flex-shrink-0 text-red-500 rounded-full p-0.5!"
                :title="selectedCanvasItem ? 'Eliminar elemento' : 'Eliminar cambio'"
                v-tooltip.top="selectedCanvasItem ? 'Eliminar elemento' : 'Eliminar cambio'"
                @click="handleDeleteControl"
              >
                <template #icon>
                  <Trash2 :size="20" class="text-red-500" />
                </template>
              </Button>
            </Transition>
          </div>

          <div
            v-else
            class="grid grid-cols-[1fr_auto_1fr] items-center gap-3 transition-all duration-300"
          >
            <div class="flex items-center justify-center min-w-[40px]"></div>

            <div class="relative min-w-0 flex justify-center">
              <Transition name="fade-slide">
                <div
                  v-show="showSendControls && hasFileStrip"
                  class="flex min-w-0 items-center gap-2 overflow-x-auto px-1"
                >
                  <div
                    v-for="(file, index) in validFiles"
                    :key="`${getFileKey(file)}-${index}`"
                    class="group relative h-12 w-12 shrink-0"
                  >
                    <button
                      type="button"
                      class="relative h-full w-full cursor-pointer overflow-hidden rounded-md border"
                      :class="
                        selectedFileIndex === index
                          ? 'border-2 border-[var(--p-primary-color)] '
                          : ''
                      "
                      :title="file.name"
                      @click="selectPreviewFile(index)"
                    >
                      <img
                        v-if="isImage(file)"
                        :src="getFilePreviewUrl(file)"
                        :alt="file.name"
                        class="h-full w-full object-cover"
                      />
                      <div v-else-if="isVideo(file)" class="relative h-full w-full bg-black">
                        <video
                          :src="getFilePreviewUrl(file)"
                          class="h-full w-full object-cover"
                          muted
                        />
                        <Video
                          :size="18"
                          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white"
                        />
                      </div>
                      <ImageIcon
                        v-else-if="isImageCategory"
                        :size="20"
                        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[var(--p-surface-500)]"
                      />
                      <component
                        :is="getFileIcon(file)"
                        v-else
                        :size="22"
                        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[var(--p-surface-500)]"
                      />
                    </button>

                    <button
                      v-if="isImage(file)"
                      type="button"
                      class="absolute cursor-pointer right-0 top-0 z-10 flex h-4 w-4 translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity hover:bg-red-500 group-hover:opacity-100"
                      :aria-label="`Eliminar ${file.name}`"
                      title="Eliminar imagen"
                      @click.stop="removeImage(index)"
                    >
                      <X :size="10" :stroke-width="3" />
                    </button>
                  </div>

                  <button
                    type="button"
                    class="flex h-12 w-12 cursor-pointer shrink-0 items-center justify-center rounded-md border border-dashed border-[var(--p-surface-400)] text-[var(--p-surface-500)] transition hover:border-[var(--p-primary-color)] hover:text-[var(--p-primary-color)] dark:border-[var(--p-surface-600)] dark:text-[var(--p-surface-300)]"
                    :title="addFileTitle"
                    @click="fileInputRef?.click()"
                  >
                    <Plus :size="24" />
                  </button>
                </div>
              </Transition>
            </div>

            <input
              ref="fileInputRef"
              type="file"
              :accept="currentAccept"
              multiple
              class="hidden"
              @change="handleAdditionalFilesSelect"
            />

            <div class="relative flex items-center justify-end min-w-[40px]">
              <Transition name="fade-slide">
                <Button
                  v-show="showSendControls"
                  text
                  class="min-h-10 min-w-10 flex-shrink-0 text-[var(--p-primary-color)] !rounded-full p-0.5!"
                  :disabled="isSending"
                  title="Enviar"
                  @click="handleSend"
                  v-tooltip.top="$t('whatsapp.actions.send_message')"
                >
                  <template #icon>
                    <IconSend width="22" height="22" class="text-[var(--p-primary-color)]" />
                  </template>
                </Button>
              </Transition>
            </div>
          </div>
        </footer>
      </div>

      <div v-else class="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <FileWarning :size="42" class="text-amber-300" />
        <p class="text-sm font-medium text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)]">
          {{ $t('whatsapp.file_upload.preview.no_valid_files') }}
        </p>
        <p class="max-w-md text-xs text-[var(--p-surface-400)] dark:text-[var(--p-surface-300)]">
          {{ $t('whatsapp.file_upload.preview.supported_formats') }}: JPG, JPEG, PNG, MP4, MOV, MP3,
          OGG, PDF, DOCX, XLSX, ZIP.
        </p>
      </div>
    </section>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import { useConfirm } from 'primevue/useconfirm'
import {
  Check,
  File,
  FileArchive,
  FileText,
  FileWarning,
  Image as ImageIcon,
  Music,
  Plus,
  Redo2,
  Trash2,
  Undo2,
  Video,
  X,
} from 'lucide-vue-next'
import type { Component } from 'vue'
import AppTextarea from '@/components/atoms/textarea/AppTextarea.vue'
import EmojiPicker from '@/components/atoms/emoji/EmojiPicker.vue'
import EditorToolbar from './EditorToolbar.vue'
import ImageCanvas from './ImageCanvas.vue'
import IconSend from '@/assets/svg/send_icon.svg?component'

type CropRatio = 'free' | '1:1' | '4:3' | '3:4' | '16:9' | '9:16' | '3:2' | '2:3' | '21:9'
type FileCategory = 'media' | 'image' | 'video' | 'audio' | 'document' | 'all'
type TextStylePayload = {
  color: string
  fontSize: number
  fontFamily: string
  textAlign: string
}

interface Props {
  files: File[]
  visible: boolean
  category?: FileCategory
}

interface Emits {
  'update:visible': [value: boolean]
  send: [files: File[], captions: string[]]
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const confirm = useConfirm()

const SUPPORTED_FORMATS = {
  image: ['image/jpeg', 'image/jpg', 'image/png'],
  video: ['video/mp4', 'video/3gp', 'video/3gpp', 'video/quicktime'],
  audio: [
    'audio/mpeg',
    'audio/mp3',
    'audio/ogg',
    'audio/amr',
    'audio/aac',
    'audio/wav',
    'audio/x-wav',
    'audio/webm',
  ],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'text/plain',
    'text/csv',
    'text/rtf',
    'application/rtf',
    'application/zip',
    'application/x-zip-compressed',
    'application/x-rar-compressed',
    'application/x-7z-compressed',
    'application/json',
    'application/xml',
    'text/xml',
  ],
}

const isVisible = ref(props.visible)
const captions = ref<string[]>([])
const localFiles = ref<File[]>([])
const selectedFileIndex = ref(0)
const previewUrls = ref<Map<string, string>>(new Map())
const activeTool = ref('')
const editorColor = ref('#3b82f6') // Default color for drawing and text
const brushWidth = ref(8)
const fontSize = ref(32)
const fontFamily = ref('Arial')
const textAlign = ref('left')
const cropRatio = ref<CropRatio>('free')
const censorMode = ref<'blur' | 'pixelate'>('blur')
const censorLevel = ref(0.35)
const isSending = ref(false)
const applyTick = ref(0)
const openTextPanelTick = ref(0)
const hasUnappliedChanges = ref(false)
const selectedCanvasItem = ref(false)
const isCloseConfirmationOpen = ref(false)
const historyState = ref({ canUndo: false, canRedo: false })
const imageCanvasRef = ref<InstanceType<typeof ImageCanvas>>()
const fileInputRef = ref<HTMLInputElement>()

const supportedFiles = computed(() => {
  const allSupported = [
    ...SUPPORTED_FORMATS.image,
    ...SUPPORTED_FORMATS.video,
    ...SUPPORTED_FORMATS.audio,
    ...SUPPORTED_FORMATS.document,
  ]
  return localFiles.value.filter((file) => allSupported.includes(file.type))
})

const validFiles = computed(() => supportedFiles.value)
const currentFile = computed(() => validFiles.value[selectedFileIndex.value] ?? validFiles.value[0])
const isCurrentImage = computed(() => Boolean(currentFile.value && isImage(currentFile.value)))
const showSendControls = computed(
  () => !isCurrentImage.value || (!hasUnappliedChanges.value && !selectedCanvasItem.value),
)
const showDeleteControl = computed(
  () => isCurrentImage.value && (hasUnappliedChanges.value || selectedCanvasItem.value),
)
const hasFileStrip = computed(() => validFiles.value.length > 0)
const currentFileKey = computed(() => (currentFile.value ? getFileKey(currentFile.value) : 'empty'))
const fileCategory = computed<FileCategory>(
  () => props.category ?? inferFileCategory(validFiles.value[0]),
)
const isImageCategory = computed(
  () => fileCategory.value === 'image' || fileCategory.value === 'media',
)
const currentAccept = computed(() => getAcceptForCategory(fileCategory.value))
const addFileTitle = computed(() => {
  if (fileCategory.value === 'audio') return 'Agregar audio'
  if (fileCategory.value === 'document') return 'Agregar documento'
  if (fileCategory.value === 'video') return 'Agregar video'
  if (fileCategory.value === 'image') return 'Agregar imagen'
  return 'Agregar archivo'
})
const currentCaption = computed({
  get: () => captions.value[selectedFileIndex.value] ?? '',
  set: (value: string) => {
    captions.value[selectedFileIndex.value] = value
  },
})

watch(
  () => props.visible,
  (newVal) => {
    isVisible.value = newVal
    if (newVal) {
      activeTool.value = ''
      selectedFileIndex.value = 0
      localFiles.value = props.files.filter((file) => isSupportedFile(file))
      captions.value = localFiles.value.map(() => '')
      hasUnappliedChanges.value = false
      historyState.value = { canUndo: false, canRedo: false }
      createPreviewUrls()
    }
  },
)

watch(isVisible, (newVal) => {
  emit('update:visible', newVal)
})

const createPreviewUrls = () => {
  revokePreviewUrls()
  validFiles.value.forEach((file) => {
    if (isImage(file) || isVideo(file) || isAudio(file)) {
      previewUrls.value.set(getFileKey(file), URL.createObjectURL(file))
    }
  })
}

const revokePreviewUrls = () => {
  previewUrls.value.forEach((url) => URL.revokeObjectURL(url))
  previewUrls.value.clear()
}

const getFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`
const getFilePreviewUrl = (file: File): string => previewUrls.value.get(getFileKey(file)) || ''
const isImage = (file: File): boolean => SUPPORTED_FORMATS.image.includes(file.type)
const isVideo = (file: File): boolean => SUPPORTED_FORMATS.video.includes(file.type)
const isAudio = (file: File): boolean => SUPPORTED_FORMATS.audio.includes(file.type)
const isDocument = (file: File): boolean => SUPPORTED_FORMATS.document.includes(file.type)
const isSupportedFile = (file: File): boolean =>
  isImage(file) || isVideo(file) || isAudio(file) || isDocument(file)

const inferFileCategory = (file?: File): FileCategory => {
  if (!file) return 'all'
  if (isImage(file)) return 'media'
  if (isVideo(file)) return 'media'
  if (isAudio(file)) return 'audio'
  if (isDocument(file)) return 'document'
  return 'all'
}

const getAcceptForCategory = (category: FileCategory): string => {
  if (category === 'media') return '.jpg,.jpeg,.png,image/jpeg,image/png,video/*,.mov'
  if (category === 'image') return '.jpg,.jpeg,.png,image/jpeg,image/png'
  if (category === 'video') return 'video/*,.mov,.mp4,.3gp'
  if (category === 'audio')
    return '.aac,.amr,.mp3,.m4a,.ogg,audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg'
  if (category === 'document') return '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z'
  return '*'
}

const matchesCurrentCategory = (file: File): boolean => {
  if (fileCategory.value === 'media') return isImage(file) || isVideo(file)
  if (fileCategory.value === 'image') return isImage(file)
  if (fileCategory.value === 'video') return isVideo(file)
  if (fileCategory.value === 'audio') return isAudio(file)
  if (fileCategory.value === 'document') return isDocument(file)
  return true
}

const getFileIcon = (file: File): Component => {
  if (isImage(file)) return ImageIcon
  if (isVideo(file)) return Video
  if (isAudio(file)) return Music
  if (
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed' ||
    file.type === 'application/x-rar-compressed' ||
    file.type === 'application/x-7z-compressed'
  ) {
    return FileArchive
  }
  if (SUPPORTED_FORMATS.document.includes(file.type)) return FileText
  return File
}

const setTool = async (tool: string) => {
  if (activeTool.value === 'crop' && tool !== 'crop') {
    await imageCanvasRef.value?.applyChanges()
  }
  activeTool.value = activeTool.value === tool && tool !== 'text' ? '' : tool
  if (tool === 'text') {
    imageCanvasRef.value?.addText()
    openTextPanelTick.value += 1
  }
  hasUnappliedChanges.value = true
}

const applyFilter = (filter: string) => {
  if (!filter) return
  imageCanvasRef.value?.applyFilter(filter)
  hasUnappliedChanges.value = true
}

const setEditorColor = (color: string) => {
  editorColor.value = color
  hasUnappliedChanges.value = true
}

const setBrushWidth = (width: number) => {
  brushWidth.value = width
  hasUnappliedChanges.value = true
}

const setFontSize = (size: number) => {
  fontSize.value = size
  hasUnappliedChanges.value = true
}

const setFontFamily = (family: string) => {
  fontFamily.value = family
  hasUnappliedChanges.value = true
}

const setTextAlign = (align: string) => {
  textAlign.value = align
  hasUnappliedChanges.value = true
}

const setCropRatio = (ratio: string) => {
  cropRatio.value = ratio as CropRatio
  hasUnappliedChanges.value = true
}

const rotateLeft = async () => {
  await imageCanvasRef.value?.rotateLeft()
  hasUnappliedChanges.value = true
}

const rotateRight = async () => {
  await imageCanvasRef.value?.rotateRight()
  hasUnappliedChanges.value = true
}

const undoCrop = async () => {
  await imageCanvasRef.value?.undoCrop()
  activeTool.value = 'crop'
  hasUnappliedChanges.value = true
}

const addEmoji = (emoji: string) => {
  imageCanvasRef.value?.addEmoji(emoji)
  hasUnappliedChanges.value = true
}

const setCensorLevel = (level: number) => {
  censorLevel.value = level
  imageCanvasRef.value?.setCensorLevel(level)
  hasUnappliedChanges.value = true
}

const setCensorMode = (mode: 'blur' | 'pixelate') => {
  censorMode.value = mode
  imageCanvasRef.value?.setCensorMode(mode)
  hasUnappliedChanges.value = true
}

const syncSelectedCensorStyle = (style: { mode: 'blur' | 'pixelate'; level: number }) => {
  censorMode.value = style.mode
  censorLevel.value = style.level
}

const openTextPanelForSelectedText = () => {
  activeTool.value = 'text'
  hasUnappliedChanges.value = true
  openTextPanelTick.value += 1
}

const syncSelectedTextStyle = (style: TextStylePayload) => {
  editorColor.value = style.color
  fontSize.value = style.fontSize
  fontFamily.value = style.fontFamily
  textAlign.value = style.textAlign
}

const exportCurrentImageEdits = async () => {
  if (!isCurrentImage.value || !currentFile.value || !imageCanvasRef.value) return
  const editedFile = await imageCanvasRef.value.exportFile()
  if (!editedFile) return
  return editedFile
}

const persistCurrentImageEdits = async () => {
  const editedFile = await exportCurrentImageEdits()
  if (!editedFile) return
  localFiles.value.splice(selectedFileIndex.value, 1, editedFile)
  createPreviewUrls()
}

const applyChanges = async () => {
  await imageCanvasRef.value?.applyChanges()
  activeTool.value = ''
  hasUnappliedChanges.value = false
  applyTick.value += 1
}

const handleCanvasOutsidePress = () => {
  if (!hasUnappliedChanges.value) return
  void applyChanges()
}

const discardUnappliedChanges = async () => {
  await imageCanvasRef.value?.discardLastChange()
  activeTool.value = ''
  hasUnappliedChanges.value = false
  applyTick.value += 1
}

const handleDeleteControl = () => {
  if (selectedCanvasItem.value) {
    imageCanvasRef.value?.deleteActiveObject()
    selectedCanvasItem.value = false
    hasUnappliedChanges.value = true
    return
  }
  void discardUnappliedChanges()
}

const selectPreviewFile = async (index: number) => {
  if (index === selectedFileIndex.value) return
  const previousIndex = selectedFileIndex.value

  if (hasUnappliedChanges.value) await applyChanges()
  const editedFile = await exportCurrentImageEdits()
  selectedFileIndex.value = index
  if (editedFile) {
    localFiles.value.splice(previousIndex, 1, editedFile)
    createPreviewUrls()
  }
  activeTool.value = ''
  hasUnappliedChanges.value = false
  historyState.value = { canUndo: false, canRedo: false }
}

const removeImage = (index: number) => {
  const file = validFiles.value[index]
  if (!file || !isImage(file)) return

  imageCanvasRef.value?.disposeCanvas()
  localFiles.value.splice(index, 1)
  captions.value.splice(index, 1)

  if (selectedFileIndex.value > index) {
    selectedFileIndex.value -= 1
  } else if (selectedFileIndex.value >= localFiles.value.length) {
    selectedFileIndex.value = Math.max(0, localFiles.value.length - 1)
  }

  activeTool.value = ''
  hasUnappliedChanges.value = false
  selectedCanvasItem.value = false
  historyState.value = { canUndo: false, canRedo: false }
  createPreviewUrls()

  if (localFiles.value.length === 0) {
    closePreview()
  }
}

const handleAdditionalFilesSelect = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files ?? []).filter(
    (file) => isSupportedFile(file) && matchesCurrentCategory(file),
  )
  if (!files.length) return
  if (hasUnappliedChanges.value) await applyChanges()
  await persistCurrentImageEdits()
  localFiles.value.push(...files)
  captions.value.push(...files.map(() => ''))
  selectedFileIndex.value = localFiles.value.length - files.length
  activeTool.value = ''
  hasUnappliedChanges.value = false
  historyState.value = { canUndo: false, canRedo: false }
  createPreviewUrls()
  input.value = ''
}

const appendCaptionEmoji = (emoji: string) => {
  currentCaption.value = `${currentCaption.value}${emoji}`
}

const handleSend = async () => {
  if (!currentFile.value || isSending.value) return
  isSending.value = true
  await persistCurrentImageEdits()
  emit(
    'send',
    validFiles.value,
    validFiles.value.map((_, index) => (captions.value[index] ?? '').trim()),
  )
  closePreview()
  isSending.value = false
}

const closePreview = () => {
  imageCanvasRef.value?.disposeCanvas()
  revokePreviewUrls()
  isVisible.value = false
  isCloseConfirmationOpen.value = false
  emit('close')
}

const requestClose = () => {
  if (isCloseConfirmationOpen.value) return
  isCloseConfirmationOpen.value = true

  confirm.require({
    message: 'Quieres descartas la seleccion?',
    header: 'Descartar seleccion',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Descartar',
    rejectLabel: 'Cancelar',
    acceptProps: {
      severity: 'danger',
    },
    rejectProps: {
      severity: 'secondary',
      outlined: true,
    },
    accept: closePreview,
    reject: () => {
      isCloseConfirmationOpen.value = false
    },
    onHide: () => {
      isCloseConfirmationOpen.value = false
    },
  })
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (!isVisible.value || event.key !== 'Escape') return
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
  requestClose()
}

onMounted(() => {
  window.addEventListener('keydown', handleEscapeKey, { capture: true })
})

onUnmounted(() => {
  imageCanvasRef.value?.disposeCanvas()
  revokePreviewUrls()
  window.removeEventListener('keydown', handleEscapeKey, { capture: true })
})
</script>

<style scoped>
.file-preview-enter-active,
.file-preview-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms ease;
}

.file-preview-enter-from,
.file-preview-leave-to {
  opacity: 0;
  transform: translateY(0) scale(0.98);
}

/* Efecto de desaparicion */
/* Duración y curva de la animación */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

/* IMPORTANTE: Al salir, se extrae del flujo para no mover a los hermanos */
.fade-slide-leave-active {
  position: absolute;
}

/* Entrada: Viene desde abajo */
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.fade-slide-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* Salida: Desaparece hacia arriba (Efecto en reversa) */
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
